/**
 * PaintBranch — interior, exterior, or both. Paint splits into two service
 * lines (PAINT_INT / PAINT_EXT). "Both" runs the project loop: interior as one
 * room, exterior as the next, under one project and one free estimate — the
 * same mechanism as multiple bathrooms.
 */
import { useLocation, useNavigate } from 'react-router-dom';

// Product codes default to the primary paint product per line; the flow can
// refine within. Kept here so the picker stays declarative.
const PAINT_INT_PRODUCT = 'PAINT_INT_UNIT';
const PAINT_EXT_PRODUCT = 'PAINT_EXT_UNIT';

interface PaintState {
  masterId?: string;
}

export function PaintBranch(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { masterId } = (location.state as PaintState) ?? {};

  function interiorOnly(): void {
    navigate('/design/project-start', {
      state: { serviceLine: 'PAINT_INT', productCode: PAINT_INT_PRODUCT, ...(masterId ? { masterId } : {}) },
    });
  }

  function exteriorOnly(): void {
    navigate('/design/project-start', {
      state: { serviceLine: 'PAINT_EXT', productCode: PAINT_EXT_PRODUCT, ...(masterId ? { masterId } : {}) },
    });
  }

  function both(): void {
    // A two-pass project: interior first, then exterior — each pass its OWN
    // service line, carried per-room so the exterior pass loads exterior questions.
    navigate('/design/project-start', {
      state: {
        serviceLine: 'PAINT_INT',
        productCode: PAINT_INT_PRODUCT,
        fixedLabels: ['Interior paint', 'Exterior paint'],
        fixedServiceLines: ['PAINT_INT', 'PAINT_EXT'],
        fixedProductCodes: [PAINT_INT_PRODUCT, PAINT_EXT_PRODUCT],
        ...(masterId ? { masterId } : {}),
      },
    });
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Inside, outside, or both?</h1>
        <p className="stage-card__lede">
          Interior and exterior are scoped differently, so we ask about them separately.
        </p>
        <div className="options" role="group" aria-label="Paint scope">
          <button type="button" className="pb-option" onClick={interiorOnly}>
            <span style={{ flexGrow: 1 }}>
              <span className="pb-option__label">Interior</span>
              <span className="pb-option__sub">Rooms, ceilings, trim, doors</span>
            </span>
          </button>
          <button type="button" className="pb-option" onClick={exteriorOnly}>
            <span style={{ flexGrow: 1 }}>
              <span className="pb-option__label">Exterior</span>
              <span className="pb-option__sub">Siding, trim, soffits, weather-dependent</span>
            </span>
          </button>
          <button type="button" className="pb-option" onClick={both}>
            <span style={{ flexGrow: 1 }}>
              <span className="pb-option__label">Both</span>
              <span className="pb-option__sub">We&rsquo;ll scope each, under one project</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
